/**
 * HTTP-backed {@link LeaderboardApi} implementation usable by any host.
 *
 * Firebase-free: the caller injects `callRemote` (PlaygroundHost passes
 * `callRemoteFunction`; platform shells pass their own authenticated invoker),
 * so importing this file does NOT pull firebase into a host bundle. Do NOT
 * import from barrel exports (leaderboard/index.ts).
 */
import {
  LeaderboardApi,
  ScoreToken,
  SubmitScoreParams,
  SubmitScoreResult,
  GetPagedScoresOptions,
  PagedScoresResponse,
  PlayerRankOptions,
  PlayerRankResult,
  GetPodiumScoresOptions,
  PodiumScoresResponse,
} from './LeaderboardApi'
import type { RemoteFunctionInvoker } from '../http/callRemoteFunction'
import { normalizeScoresResponse } from './normalizeScores'
import { assertReplayWithinInlineCap } from './replaySize'
import { mapSubmitScoreFailure } from './utils'

/**
 * HTTP-based implementation of LeaderboardApi.
 */
export class HttpLeaderboardApi implements LeaderboardApi {
  private readonly appId: string
  private readonly callRemote: RemoteFunctionInvoker

  constructor(
    appId: string,
    options: {
      callRemote: RemoteFunctionInvoker
    },
  ) {
    this.appId = appId
    this.callRemote = options.callRemote
  }

  async createScoreToken(mode?: string): Promise<ScoreToken> {
    return this.callRemote<ScoreToken>('apiCreateScoreToken', {
      appId: this.appId,
      mode: mode || 'default',
    })
  }

  async submitScore(params: SubmitScoreParams): Promise<SubmitScoreResult> {
    if (params.replay !== undefined) {
      assertReplayWithinInlineCap(params.replay)
    }
    try {
      return await this.callRemote<SubmitScoreResult>('apiSubmitScore', {
        appId: this.appId,
        token: params.token,
        score: params.score,
        duration: params.duration,
        mode: params.mode || 'default',
        period: params.period,
        telemetry: params.telemetry,
        metadata: params.metadata,
        // Score-sealing hash auto-computed by RpcLeaderboardApi; without it sealed
        // leaderboards reject the score.
        hash: params.hash,
        // The recorded replay for a requiresReplay board; the server re-runs the
        // certified runtime over it before ranking the score.
        replay: params.replay,
      })
    } catch (error) {
      // Resolve identity-change / replay-rejection to a typed result so HTTP
      // callers (playground) complete the same pending → ranked/rejected flow as
      // the RPC path. Genuine failures still throw.
      const mapped = mapSubmitScoreFailure(error)
      if (mapped) {
        return mapped
      }
      throw error
    }
  }

  async getPagedScores(options?: GetPagedScoresOptions): Promise<PagedScoresResponse> {
    const res = await this.callRemote<PagedScoresResponse>('apiGetPagedScores', {
      appId: this.appId,
      mode: options?.mode || 'default',
      period: options?.period || 'alltime',
      periodDate: options?.periodDate,
      cursor: options?.cursor,
      limit: options?.limit,
      variant: options?.variant || 'standard',
      topCount: options?.topCount,
      contextAhead: options?.contextAhead,
      contextBehind: options?.contextBehind,
    })
    return normalizeScoresResponse(res)
  }

  async getMyRank(options?: PlayerRankOptions): Promise<PlayerRankResult> {
    const res = await this.callRemote<PlayerRankResult>('apiGetMyRank', {
      appId: this.appId,
      mode: options?.mode || 'default',
      period: options?.period || 'alltime',
      periodDate: options?.periodDate,
    })
    return normalizeScoresResponse(res)
  }

  async getPodiumScores(options?: GetPodiumScoresOptions): Promise<PodiumScoresResponse> {
    const res = await this.callRemote<PodiumScoresResponse>('apiGetPodiumScores', {
      appId: this.appId,
      mode: options?.mode || 'default',
      period: options?.period || 'alltime',
      periodDate: options?.periodDate,
      topCount: options?.topCount,
      contextAhead: options?.contextAhead,
      contextBehind: options?.contextBehind,
    })
    return normalizeScoresResponse(res)
  }
}
